import CargoCompanyOrderBillingsListParams from "../../../../../common/services/cargo-company/order-billings/list/cargo-company-order-billings-list-params";
import BillingStatus from "../../../../../common/types/billing-status";
import CargoCompanyOrder from "../../../../../common/types/cargo-company-order";
import paginationService from "../../../../../common/utils/pagination/pagination.service";
import BillingsCargoOrderListingFilter, {
  BillingsCargoOrderListingBillingStatusFilter,
  BillingsCargoOrderListingDispatchFilter,
  BillingsCargoOrderListingExternalOrderIdFilter,
  BillingsCargoOrderListingInternalOrderIdFilter,
  BillingsCargoOrderListingOrderStartDateFilter,
  BillingsCargoOrderListingPassengerFilter,
  BillingsCargoOrderListingRouteAddressFilter,
  BillingsCargoOrderListingRouteDestinationAddressFilter,
  BillingsCargoOrderListingRouteIntermediateAddressFilter,
  BillingsCargoOrderListingRoutePickupAddressFilter,
  BillingsCargoOrderListingTaxiFilter,
} from "./types/billings-cargo-order-listing-filter";
import BillingsCargoOrderListingFilterType from "./types/billings-cargo-order-listing-filter-type";
import BillingsCargoOrderListingItemBillingStatus from "./types/billings-cargo-order-listing-item-billing-status";
import BillingsCargoOrderListingSortKey from "./types/billings-cargo-order-listing-sort-key";

const createStatus = (
  status: BillingsCargoOrderListingItemBillingStatus | undefined,
): BillingStatus | undefined => {
  switch (status) {
    case BillingsCargoOrderListingItemBillingStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsCargoOrderListingItemBillingStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsCargoOrderListingItemBillingStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsCargoOrderListingItemBillingStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsCargoOrderListingItemBillingStatus.REOPEN_REQUEST_SENT:
      return BillingStatus.REOPEN_REQUEST;
    default:
      return undefined;
  }
};

const createRequestOrder = (
  sortKey: BillingsCargoOrderListingSortKey | null,
): CargoCompanyOrderBillingsListParams["order"] => {
  const options: {
    sortKey: BillingsCargoOrderListingSortKey;
    requestOrder: CargoCompanyOrder;
  }[] = [
    {
      requestOrder: CargoCompanyOrder.DATE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.ORDER_START_DATE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DATE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.ORDER_START_DATE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HUMAN_ID_ASC,
      sortKey: BillingsCargoOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HUMAN_ID_DESC,
      sortKey: BillingsCargoOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.TAXI_CORPORATION_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TAXI_CORPORATION_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.TAXI_CORPORATION_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TAXI_CORPORATION_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.EXTERNAL_ORDER_ID_ASC,
      sortKey: BillingsCargoOrderListingSortKey.EXTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.EXTERNAL_ORDER_ID_DESC,
      sortKey: BillingsCargoOrderListingSortKey.EXTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISPATCH_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISPATCH_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISPATCH_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISPATCH_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_RATE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_RATE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_RATE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_RATE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_HOURS_ASC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_TIME_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_HOURS_DESC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_TIME_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_AMOUNT_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_AMOUNT_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_SAVINGS_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_SAVINGS_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.TOTAL_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.TOTAL_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TOTAL_COST_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createRequest = (
  page: number,
  pageSize: number,
  cargoCompanyUuid: string,
  filters: BillingsCargoOrderListingFilter[],
  sortKey: BillingsCargoOrderListingSortKey | null,
): CargoCompanyOrderBillingsListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    limit: pageSize,
    offset,
    cargoCompanyUuid,
    order: createRequestOrder(sortKey),
    humanId: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsCargoOrderListingInternalOrderIdFilter["value"]
      | undefined,
    address: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteAddressFilter["value"]
      | undefined,
    startAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRoutePickupAddressFilter["value"]
      | undefined,
    midAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteIntermediateAddressFilter["value"]
      | undefined,
    endAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteDestinationAddressFilter["value"]
      | undefined,
    passenger: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.PASSENGER,
    )?.value as BillingsCargoOrderListingPassengerFilter["value"] | undefined,
    taxi: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.TAXI,
    )?.value as BillingsCargoOrderListingTaxiFilter["value"] | undefined,
    externalId: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.EXTERNAL_ORDER_ID,
    )?.value as
      | BillingsCargoOrderListingExternalOrderIdFilter["value"]
      | undefined,
    dispatch: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.DISPATCH,
    )?.value as BillingsCargoOrderListingDispatchFilter["value"] | undefined,
    status: createStatus(
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.BILLING_STATUS,
      )?.value as
        | BillingsCargoOrderListingBillingStatusFilter["value"]
        | undefined,
    ),
    dateSince: (
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsCargoOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    dateTo: (
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsCargoOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
  };
};

const billingsCargoOrderListingRequestFactory = {
  createRequest,
};

export default billingsCargoOrderListingRequestFactory;
